/**
 * useSnoozeStore — FE-side "snooze" state for inbox messages.
 *
 * Why FE-side (June 2026)
 * -----------------------
 * The BE doesn't yet expose a ``snoozed_until`` column on
 * ``content_items`` or the live feed. Until it does, this store
 * persists snooze decisions in ``localStorage`` so the user gets the
 * Superhuman/Hey workflow today: pick a preset → row disappears from
 * the inbox → reappears at the chosen time.
 *
 * The data here is **session-affordance**, not source-of-truth: a
 * cleared cache resurfaces every snoozed row, which is the
 * correct-but-coarse behaviour (no data loss, the user just sees
 * the messages again). When the BE field lands, this store collapses
 * to a thin cache layer above the new API — the public hook surface
 * stays the same so call sites don't change.
 *
 * Pattern reference
 * -----------------
 * Mirrors :file:`features/chat/state/chatLayoutStore.ts` for the
 * Zustand + ``persist`` + ``createJSONStorage`` setup. The state shape
 * is a plain object ``{ [messageId]: epochMs }`` so JSON
 * serialisation is trivial and lookups stay O(1).
 *
 * Auto-eviction
 * -------------
 * The selector hook runs an eviction pass on read: any entry whose
 * ``epochMs`` is in the past gets dropped so the inbox naturally
 * un-snoozes at the chosen time without a setInterval. This keeps
 * the store quiet (no rerender clock).
 */

import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useEffect, useMemo } from "react";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";


interface SnoozeStoreState {
  /** ``message_id → epoch-ms`` when the row should reappear. */
  readonly entries: Readonly<Record<string, number>>;
  /** Snooze a single message until ``epochMs``. */
  readonly snooze: (messageId: string, epochMs: number) => void;
  /** Snooze many at once (bulk action). */
  readonly snoozeMany: (
    ids: ReadonlyArray<string>,
    epochMs: number,
  ) => void;
  /** Cancel a snooze (e.g. the user un-snoozes from the empty-state). */
  readonly cancel: (messageId: string) => void;
  /** Drop every entry whose epoch is ``≤ nowMs``. Idempotent. */
  readonly evictExpired: (nowMs: number) => void;
}


export const useSnoozeStore = create<SnoozeStoreState>()(
  persist(
    (set, get) => ({
      entries: {},
      snooze: (messageId, epochMs) => {
        if (!messageId || !Number.isFinite(epochMs)) return;
        set({ entries: { ...get().entries, [messageId]: epochMs } });
      },
      snoozeMany: (ids, epochMs) => {
        if (ids.length === 0 || !Number.isFinite(epochMs)) return;
        const next = { ...get().entries };
        for (const id of ids) {
          if (id) next[id] = epochMs;
        }
        set({ entries: next });
      },
      cancel: (messageId) => {
        const cur = get().entries;
        if (!(messageId in cur)) return;
        const next = { ...cur };
        delete next[messageId];
        set({ entries: next });
      },
      evictExpired: (nowMs) => {
        const cur = get().entries;
        const next: Record<string, number> = {};
        let changed = false;
        for (const [k, v] of Object.entries(cur)) {
          if (v > nowMs) next[k] = v;
          else changed = true;
        }
        if (changed) set({ entries: next });
      },
    }),
    {
      name: "ando-inbox-snooze",
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);


// ── Hooks ──────────────────────────────────────────────────────────────


/**
 * Returns the set of message ids that should be HIDDEN from the inbox
 * right now. Eviction runs once per mount + every time the user opens
 * the route — no setInterval. Re-evicting on a focus event is cheap and
 * keeps the surface honest after a long idle.
 */
export function useSnoozedIds(): ReadonlySet<string> {
  const entries = useSnoozeStore((s) => s.entries);
  const evict = useSnoozeStore((s) => s.evictExpired);
  useEffect(() => {
    evict(Date.now());
    const onFocus = () => evict(Date.now());
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [evict]);
  return useMemo(() => {
    const now = Date.now();
    const set = new Set<string>();
    for (const [id, t] of Object.entries(entries)) {
      if (t > now) set.add(id);
    }
    return set;
  }, [entries]);
}


// ── Preset helpers ─────────────────────────────────────────────────────


export interface SnoozePreset {
  readonly id: string;
  /** i18n descriptor resolved at the render site via ``i18n._()``. */
  readonly label: MessageDescriptor;
  /** Resolve the epoch-ms target from a base ``now``. Pure for tests. */
  readonly resolve: (now: Date) => number;
}


/** Set local-time HH:MM on the SAME calendar day as ``base``. */
function atTime(base: Date, hour: number, minute = 0): Date {
  const d = new Date(base);
  d.setHours(hour, minute, 0, 0);
  return d;
}


function addHours(base: Date, hours: number): Date {
  return new Date(base.getTime() + hours * 60 * 60 * 1000);
}


function addDays(base: Date, days: number): Date {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d;
}


/**
 * Default Italian preset set — matches the Superhuman/Hey vocabulary
 * adapted for an Italian working-day rhythm (9:00 start, 18:00 close).
 *
 * Resolution rules:
 *   - ``1h``      → now + 60 min
 *   - ``stasera`` → today 18:00 (skip if already past; rolls to tomorrow 09:00)
 *   - ``domani``  → tomorrow 09:00
 *   - ``lunedi``  → next Monday 09:00 (skip if today IS Monday before 09:00)
 *   - ``settimana``→ next Monday + 7 days at 09:00
 */
export const DEFAULT_SNOOZE_PRESETS: ReadonlyArray<SnoozePreset> = [
  {
    id: "1h",
    label: msg`Tra 1 ora`,
    resolve: (now) => addHours(now, 1).getTime(),
  },
  {
    id: "stasera",
    label: msg`Stasera (18:00)`,
    resolve: (now) => {
      const target = atTime(now, 18);
      if (target.getTime() > now.getTime()) return target.getTime();
      // Past 18:00 already — roll to tomorrow 09:00.
      return atTime(addDays(now, 1), 9).getTime();
    },
  },
  {
    id: "domani",
    label: msg`Domani (9:00)`,
    resolve: (now) => atTime(addDays(now, 1), 9).getTime(),
  },
  {
    id: "lunedi",
    label: msg`Lunedì (9:00)`,
    resolve: (now) => {
      // 0=Sun, 1=Mon, … 6=Sat
      const dow = now.getDay();
      const target9 = atTime(now, 9);
      if (dow === 1 && target9.getTime() > now.getTime()) {
        return target9.getTime();
      }
      const diff = ((1 - dow + 7) % 7) || 7;
      return atTime(addDays(now, diff), 9).getTime();
    },
  },
  {
    id: "settimana",
    label: msg`Prossima settimana`,
    resolve: (now) => {
      const dow = now.getDay();
      const diff = ((1 - dow + 7) % 7) || 7;
      return atTime(addDays(now, diff + 7), 9).getTime();
    },
  },
];
